import {
  FilterDocumentTagMatchMode,
  FilterOptionKeys,
  GoalProgressCount,
  ID,
} from '@griply/types';
import { filterOptionKeysOrder, sortFilterOptionKeys } from '@griply/utils';
import { TFunction } from 'i18next';
import { DateFormatOptions } from 'shared/types/date-format-options';
import {
  FilterDateRangeOption,
  FilterGroup,
  FilterNumberRangeOption,
  FilterSelectOption,
} from 'shared/types/filter';
import { Goal } from 'shared/types/goal';
import { LifeArea } from 'shared/types/life-area';
import { Tag } from 'shared/types/tag';
import { WeekDays } from 'shared/types/week-days';
import { createDateFilter } from 'shared/utils/filters/create-date-filter';
import { createPriorityFilter } from 'shared/utils/filters/create-priority-filter';

import { createCompletedAtFilter } from './create-completed-at-filter';
import { createDeadlineFilter } from './create-deadline-filter';
import { createDurationFilter } from './create-duration-filter';
import { createGoalActiveFilter } from './create-goal-active-filter';
import { createGoalFilter } from './create-goal-filter';
import { createGoalImpactFilter } from './create-goal-impact-filter';
import { createLifeAreaFilter } from './create-life-area-filter';
import { createTagFilter } from './create-tag-filter';
import { createTypeFilter } from './create-type-filter';

export type Options = {
  t: TFunction;
  goals: Goal[];
  lifeAreas: LifeArea[];
  tags: Tag[];
  dateFormat: DateFormatOptions;
  weekStartsOn: WeekDays;
  supportedFilters?: FilterOptionKeys[];
  hideNoGoal?: boolean;
  taskCounts?: Map<ID, number>;
  goalProgressCounts?: Map<ID, GoalProgressCount>;
  lifeAreaSorting?: ID[] | null;
  currentTagMatchMode?: FilterDocumentTagMatchMode;
  labelOverrides?: Partial<Record<FilterOptionKeys, string>>;
  iconOverrides?: Partial<
    Record<FilterOptionKeys, NonNullable<FilterDateRangeOption['icon']>>
  >;
};

export const createFilters = ({
  t,
  goals,
  lifeAreas,
  tags,
  dateFormat,
  weekStartsOn,
  supportedFilters,
  hideNoGoal,
  taskCounts,
  goalProgressCounts,
  lifeAreaSorting,
  currentTagMatchMode,
  labelOverrides,
  iconOverrides,
}: Options): FilterGroup[] => {
  const filterCreators: Partial<
    Record<
      FilterOptionKeys,
      () =>
        | FilterSelectOption[]
        | FilterDateRangeOption[]
        | FilterNumberRangeOption[]
    >
  > = {
    [FilterOptionKeys.LifeArea]: () =>
      createLifeAreaFilter(lifeAreas, { t, lifeAreaSorting }),
    [FilterOptionKeys.Goal]: () =>
      createGoalFilter(goals, {
        t,
        hideNoGoal,
        taskCounts,
        goalProgressCounts,
      }),
    [FilterOptionKeys.GoalActive]: () => [
      createGoalActiveFilter({
        t,
        dateFormat,
        weekStartsOn,
      }),
    ],
    [FilterOptionKeys.Tag]: () =>
      createTagFilter(tags, { t, currentTagMatchMode }),
    [FilterOptionKeys.Date]: () => [
      createDateFilter({ t, dateFormat, weekStartsOn }),
    ],
    [FilterOptionKeys.Deadline]: () => [
      createDeadlineFilter({ t, dateFormat, weekStartsOn }),
    ],
    [FilterOptionKeys.CompletedAt]: () => [
      createCompletedAtFilter({ t, dateFormat, weekStartsOn }),
    ],
    [FilterOptionKeys.Priority]: () => [createPriorityFilter({ t })],
    [FilterOptionKeys.Duration]: () => [createDurationFilter({ t })],
    [FilterOptionKeys.Type]: () => [createTypeFilter({ t })],
    [FilterOptionKeys.GoalImpact]: () => [createGoalImpactFilter({ t })],
  };

  const order = supportedFilters
    ? sortFilterOptionKeys(supportedFilters)
    : [...filterOptionKeysOrder];

  const filters = order.reduce<
    (FilterSelectOption | FilterDateRangeOption | FilterNumberRangeOption)[]
  >((acc, key) => {
    const creator = filterCreators[key];
    if (creator && (!supportedFilters || supportedFilters.includes(key))) {
      acc.push(...creator());
    }
    return acc;
  }, []);

  const overriddenFilters =
    labelOverrides || iconOverrides
      ? filters.map((filter) => {
          const id = filter.id as FilterOptionKeys;
          const labelOverride = labelOverrides?.[id];
          const iconOverride = iconOverrides?.[id];
          if (!labelOverride && !iconOverride) {
            return filter;
          }
          return {
            ...filter,
            ...(labelOverride ? { label: labelOverride } : {}),
            ...(iconOverride ? { icon: iconOverride } : {}),
          };
        })
      : filters;

  // No supported filters (e.g. habits) → omit the whole "Filters" section
  // instead of rendering an empty header with no options.
  if (!overriddenFilters.length) {
    return [];
  }

  return [
    {
      id: 'filters',
      title: t('context-menus.filtering.task.filters.title'),
      filters: overriddenFilters,
    },
  ];
};
